import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  applyEach,
  disabled,
  form,
  FormField,
  min,
  required,
} from '@angular/forms/signals';
import { Partner } from '@core/core.types';
import { momentToNgbDate } from '@core/utils/core.util';
import {
  NgbActiveModal,
  NgbDatepickerModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { PartnerTypeaheadComponent } from '@shared/components/partner-typeahead/partner-typeahead.component';
import { ToastService } from '@shared/components/toast-container/toast.service';
import moment from 'moment';
import { finalize, map, Observable, of, switchMap } from 'rxjs';
import { PricingCountryService } from '../../../services/pricing-country.service';
import { PricingPartnerService } from '../../../services/pricing-partner.service';
import { AddPricingPartnerBundleAction } from '../../../store/pricing-partner.actions';
import { PricingPartnerState } from '../../../store/pricing-partner.state';
import {
  BundlePrice,
  CountryBundlePrice,
  CountryPriceType,
  PricingPartner,
} from '../../../types/pricing.types';

interface DoctorsFeeRow {
  bundleId: string;
  name: string;
  currentWsp: number | null;
  eup: number | null;
}

interface DoctorsFeeModel {
  validFrom: moment.Moment | null;
  fees: { bundleId: string; fee: number | null; eupMissing: boolean }[];
}

/**
 * Mass-edit of partner WSP prices derived from the country EUP minus a
 * doctor's fee per bundle: `new WSP = EUP - doctor's fee`. The fee itself is
 * not stored by the backend; the resulting WSP prices are saved as regular
 * partner bundle prices.
 */
@Component({
  selector: 'app-doctors-fee-dialog',
  imports: [
    FormField,
    ReactiveFormsModule,
    TranslateModule,
    NgbDatepickerModule,
    NgbTooltipModule,
    PartnerTypeaheadComponent,
    DecimalPipe,
  ],
  templateUrl: './doctors-fee-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './doctors-fee-dialog.component.scss',
})
export class DoctorsFeeDialogComponent {
  public partnerId = signal<string>(null);

  protected modalRef = inject(NgbActiveModal);
  private store = inject(Store);
  private pricingPartnerService = inject(PricingPartnerService);
  private pricingCountryService = inject(PricingCountryService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);

  protected pricingPartner = computed<PricingPartner>(() =>
    this.store.selectSignal(PricingPartnerState.getPricingPartnerById)()(
      this.partnerId()
    )
  );
  protected currencyMissing = computed(() => !this.pricingPartner()?.currency);

  protected rows = computed<DoctorsFeeRow[]>(() =>
    (this.pricingPartner()?.pricelist ?? [])
      .map((bundle: BundlePrice) => ({
        bundleId: bundle.id,
        name: bundle.internalName ?? bundle.name,
        currentWsp: bundle.pivot?.price ?? null,
        eup: bundle.countryEupPrice ?? null,
      }))
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      )
  );

  protected feeModel = signal<DoctorsFeeModel>({ validFrom: null, fees: [] });
  protected feeForm = form(this.feeModel, (path) => {
    required(path.validFrom);
    applyEach(path.fees, (feePath) => {
      min(feePath.fee, 0);
      // Without a country EUP the new WSP cannot be calculated, so the fee
      // is not enterable for that bundle.
      disabled(feePath.fee, ({ valueOf }) => valueOf(feePath.eupMissing));
    });
  });

  // The shared partner typeahead still expects a FormControl.
  protected copyPartnerControl = new FormControl<string>(null);
  protected copyingFromCountry = signal<boolean>(false);

  protected minDate = moment().add(1, 'day');
  protected maxDate = moment().add(100, 'year');
  protected startDate = moment().add(1, 'month');
  protected readonly momentToNgbDate = momentToNgbDate;

  constructor() {
    // (Re)build the fee rows when the partner's bundle set changes; the
    // initial fee is the difference between the current WSP and the EUP.
    effect(() => {
      const rows = this.rows();
      const current = this.feeModel().fees;
      const sameBundles =
        current.length === rows.length &&
        current.every((fee, index) => fee.bundleId === rows[index].bundleId);

      if (sameBundles) {
        return;
      }

      this.feeModel.update((model) => ({
        ...model,
        fees: rows.map((row) => ({
          bundleId: row.bundleId,
          fee: this.deriveFee(row.currentWsp, row.eup),
          eupMissing: row.eup === null,
        })),
      }));
    });
  }

  protected feeAt(index: number): number | null {
    return this.feeModel().fees[index]?.fee ?? null;
  }

  protected newWsp(row: DoctorsFeeRow, index: number): number | null {
    const fee = this.feeAt(index);

    if (row.eup === null || fee === null || fee === undefined) {
      return null;
    }

    return this.round(row.eup - Number(fee));
  }

  protected hasChanges = computed(() =>
    this.rows().some((row, index) => {
      const newWsp = this.newWsp(row, index);
      return newWsp !== null && newWsp !== row.currentWsp;
    })
  );

  protected submitDisabled = computed(
    () =>
      this.currencyMissing() ||
      !this.rows().length ||
      this.feeForm().invalid() ||
      !this.hasChanges()
  );

  protected save() {
    if (this.submitDisabled()) {
      return;
    }

    const validFrom = this.feeModel().validFrom?.toDate() ?? null;
    const actions = this.rows()
      .map((row, index) => ({ row, newWsp: this.newWsp(row, index) }))
      .filter(({ row, newWsp }) => newWsp !== null && newWsp !== row.currentWsp)
      .map(
        ({ row, newWsp }) =>
          new AddPricingPartnerBundleAction(
            this.partnerId(),
            row.bundleId,
            newWsp,
            validFrom
          )
      );

    if (!actions.length) {
      this.modalRef.close();
      return;
    }

    this.store.dispatch(actions).subscribe(() => this.modalRef.close());
  }

  /** Copies another partner's WSP prices, expressed as fees over the EUP. */
  protected onPartnerSelected(partner: Partner) {
    if (!partner) {
      return;
    }

    this.pricingPartnerService.getPricingPartner(partner.id).subscribe({
      next: (pricingPartner) => {
        const wspByBundleId = new Map(
          (pricingPartner?.pricelist ?? [])
            .filter((bundle) => bundle?.pivot?.price !== null)
            .map((bundle) => [bundle.id, bundle.pivot.price])
        );

        this.applyWspPrices(wspByBundleId);
      },
    });
  }

  /** Copies the partner's country WSP prices, expressed as fees over the EUP. */
  protected copyFromCountry(event: MouseEvent) {
    event.preventDefault();

    const countryName = this.pricingPartner()?.country;

    if (!countryName) {
      this.toastService.warning(
        this.translateService.instant(
          'pricing.partner.details.bulkEdit.copyFromCountryNoCountry'
        )
      );
      return;
    }

    this.copyingFromCountry.set(true);

    this.pricingCountryService
      .getPricingCountriesList({ name: countryName })
      .pipe(
        map((countries) =>
          countries.data.find(
            (country) =>
              country.name.toLowerCase() === countryName.toLowerCase()
          )
        ),
        switchMap(
          (country): Observable<CountryBundlePrice[] | null> =>
            country
              ? this.pricingCountryService.getPricingCountryBundles(
                  country.id,
                  CountryPriceType.WSP
                )
              : of(null)
        ),
        finalize(() => this.copyingFromCountry.set(false))
      )
      .subscribe({
        next: (bundles) => {
          if (!bundles) {
            this.toastService.warning(
              this.translateService.instant(
                'pricing.partner.details.bulkEdit.copyFromCountryNotFound',
                { country: countryName }
              )
            );
            return;
          }

          const copied = this.applyWspPrices(
            new Map(
              bundles
                .filter((bundle) => bundle?.pivot?.price !== null)
                .map((bundle) => [bundle.id, bundle.pivot.price])
            )
          );

          if (!copied) {
            this.toastService.info(
              this.translateService.instant(
                'pricing.partner.details.bulkEdit.copyFromCountryNoPrices',
                { country: countryName }
              )
            );
          }
        },
        error: (error) => {
          this.toastService.danger(error.error?.message, error.error?.error);
        },
      });
  }

  /**
   * Turns copied WSP prices into fees (`EUP - WSP`) for the rows that have an
   * EUP and a copied price. Returns the number of rows updated.
   */
  private applyWspPrices(wspByBundleId: Map<string, number>): number {
    let copied = 0;
    const rows = this.rows();

    this.feeModel.update((model) => ({
      ...model,
      fees: model.fees.map((entry, index) => {
        const row = rows[index];
        const wsp = wspByBundleId.get(entry.bundleId);

        if (!row || row.eup === null || wsp === undefined || wsp === null) {
          return entry;
        }

        copied++;
        return { ...entry, fee: this.deriveFee(wsp, row.eup) };
      }),
    }));

    return copied;
  }

  private deriveFee(wsp: number | null, eup: number | null): number | null {
    if (
      wsp === null ||
      wsp === undefined ||
      eup === null ||
      eup === undefined
    ) {
      return null;
    }

    return this.round(eup - wsp);
  }

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
